import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO, startOfWeek, endOfWeek, addDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { BarChart3, ChevronLeft, ChevronRight, Calendar as CalendarIcon, FileDown, Table2, Sparkles, IndianRupee, Users, Wheat, Printer, Layers } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as ShadcnCal } from "../components/ui/calendar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/mdmData";
import { exportPanjiPDF } from "../lib/mdmExport";
import { formatReportDate, formatRiceQuantity, formatRiceKg, CATEGORY_KEYS, CATEGORY_LABELS, CATEGORY_EMOJI, aggregateCategory } from "../lib/reportFormat";

// Colour-coded status for report rows.
function classify(e) {
  if (!e) return { key: "none", label: "Not entered", cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "⚪" };
  if ((e.entry_type || "meal") === "ladoo") return { key: "ladoo", label: "Ladoo", cls: "bg-orange-100 text-orange-800 border-orange-200", dot: "🧁" };
  if (e.day_type === "HOLIDAY") return { key: "holiday", label: "Holiday", cls: "bg-red-100 text-red-800 border-red-200", dot: "🔴" };
  if (e.meal_type === "DRY_RATION") return { key: "dry", label: "Dry Ration", cls: "bg-sky-100 text-sky-800 border-sky-200", dot: "🔵" };
  return { key: "mdm", label: "MDM", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "🟢" };
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const AY_START_MONTH = 3; // April

function academicYearRange(baseDate) {
  const y = baseDate.getMonth() >= AY_START_MONTH ? baseDate.getFullYear() : baseDate.getFullYear() - 1;
  const from = new Date(y, AY_START_MONTH, 1);
  const to = new Date(y + 1, AY_START_MONTH, 0); // last day of March next year
  return { from, to, label: `${y}-${String((y + 1) % 100).padStart(2, "0")}` };
}

export default function Reports() {
  const nav = useNavigate();
  const { user, loading, isAuthed } = useAuth();
  const [sub, setSub] = useState(null);
  const [tab, setTab] = useState("weekly");
  const [anchor, setAnchor] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  // Class-wise state
  const [cwCat, setCwCat] = useState("all");                  // "all" | one of CATEGORY_KEYS
  const [cwFrom, setCwFrom] = useState(startOfMonth(new Date()));
  const [cwTo, setCwTo] = useState(endOfMonth(new Date()));

  useEffect(() => { if (!loading && !isAuthed) nav("/login"); }, [loading, isAuthed, nav]);
  useEffect(() => { if (isAuthed) api.subMe().then(setSub).catch(() => {}); }, [isAuthed]);

  const range = useMemo(() => {
    if (tab === "class") {
      return { from: cwFrom, to: cwTo, label: `${formatReportDate(cwFrom)} to ${formatReportDate(cwTo)}` };
    }
    if (tab === "daily") {
      return { from: anchor, to: anchor, label: format(anchor, "EEEE, dd MMM yyyy") };
    }
    if (tab === "weekly") {
      const from = startOfWeek(anchor, { weekStartsOn: 1 });
      const to = endOfWeek(anchor, { weekStartsOn: 1 });
      return { from, to, label: `Week of ${format(from, "d MMM")} – ${format(to, "d MMM yyyy")}` };
    }
    if (tab === "monthly") {
      const from = startOfMonth(anchor);
      const to = endOfMonth(anchor);
      return { from, to, label: format(from, "MMMM yyyy") };
    }
    // annual: academic year
    return { ...academicYearRange(anchor), label: `AY ${academicYearRange(anchor).label}` };
  }, [tab, anchor, cwFrom, cwTo]);

  const load = async () => {
    if (!isAuthed) return;
    setBusy(true);
    try {
      const rows = await api.listDailyEntries({ from_date: iso(range.from), to_date: iso(range.to), limit: 500 });
      setEntries(rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load entries");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [isAuthed, range.from, range.to]);   // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (delta) => {
    if (tab === "daily")   setAnchor(addDays(anchor, delta));
    if (tab === "weekly")  setAnchor(addDays(anchor, delta * 7));
    if (tab === "monthly") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));
    if (tab === "annual")  setAnchor(new Date(anchor.getFullYear() + delta, anchor.getMonth(), 1));
  };

  const totals = useMemo(() => {
    let s = 0, c = 0, r = 0, l = 0, d = 0, holidays = 0, mdmDays = 0, dryDays = 0;
    entries.forEach((e) => {
      const isLadoo = (e.entry_type || "meal") === "ladoo";
      const sm = e.summary || {};
      if (isLadoo) { l += Number(sm.total_students || 0); return; }
      d += 1;
      if (e.day_type === "HOLIDAY") { holidays += 1; return; }
      s += Number(sm.total_students || 0);
      c += Number(sm.grand_total || 0);
      r += Number(sm.total_rice_kg || 0);
      if (e.meal_type === "DRY_RATION") dryDays += 1; else mdmDays += 1;
    });
    const workingDays = mdmDays + dryDays;
    return { students: s, cost: c, rice: r, ladoos: l, days: d, holidays, workingDays, mdmDays, dryDays, avg_per_day: workingDays > 0 ? c / workingDays : 0 };
  }, [entries]);

  // Group by month for annual view
  const monthGroups = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const m = e.date_iso.slice(0, 7);
      if (!map.has(m)) map.set(m, { key: m, label: format(parseISO(`${m}-01`), "MMM yyyy"), students: 0, cost: 0, rice: 0, days: 0, ladoos: 0 });
      const g = map.get(m);
      const sm = e.summary || {};
      g.days += 1;
      g.students += Number(sm.total_students || 0);
      g.cost += Number(sm.grand_total || 0);
      g.rice += Number(sm.total_rice_kg || 0);
      if ((e.entry_type || "meal") === "ladoo") g.ladoos += Number(sm.total_students || 0);
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [entries]);

  const onPdf = async () => {
    if (!entries.length) return toast.error("No entries in this period");
    try {
      await exportPanjiPDF({
        entries,
        school: { name: user?.school_name || "", udise: user?.udise || "" },
        from_date: iso(range.from),
        to_date: iso(range.to),
        monthLabel: range.label,
      });
      toast.success("Report PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); console.error(e); }
  };

  const onCsv = () => {
    if (!entries.length) return toast.error("No entries in this period");
    if (tab === "class" && cwCat !== "all") {
      // Category-specific CSV
      const label = CATEGORY_LABELS[cwCat];
      const header = ["Date", "Day", "Day Type", "Meal Type", "Category", "Attendance", "Rice (kg)", "Cooking Cost (INR)"];
      const rows = entries
        .filter((e) => (e.entry_type || "meal") === "meal")
        .map((e) => {
          const c = (e.categories || {})[cwCat] || {};
          const isHol = e.day_type === "HOLIDAY";
          return [
            formatReportDate(e.date_iso), e.weekday_name || "",
            e.day_type || "WORKING_DAY",
            e.meal_type || (isHol ? "" : "MDM"),
            label,
            isHol ? 0 : Number(c.student_count || e.counts?.[cwCat] || 0),
            isHol ? "0.000" : formatRiceKg(c.rice_kg),
            isHol ? "0.00" : Number(c.subtotal || 0).toFixed(2),
          ];
        });
      const agg = aggregateCategory(entries, cwCat);
      const csv = [header, ...rows, [], ["", "", "", "", "TOTAL", agg.attendance, agg.rice_kg.toFixed(3), agg.cost.toFixed(2)]]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
      _downloadCsv(csv, `PM_POSHAN_CLASSWISE_${cwCat.toUpperCase()}_${iso(range.from)}_${iso(range.to)}.csv`);
      return;
    }
    const header = ["Date", "Day", "Day Type", "Meal Type", "Menu", "Balvatika", "Primary", "Upper Primary", "High School", "Total Students", "Rice (kg)", "Total Cost (INR)"];
    const rows = entries.map((e) => [
      formatReportDate(e.date_iso), e.weekday_name || "",
      e.day_type || ((e.entry_type||"meal")==="ladoo" ? "" : "WORKING_DAY"),
      e.meal_type || ((e.entry_type||"meal")==="ladoo" ? "LADOO" : ""),
      (e.menu_display || "").replace(/,/g, ";"),
      e.counts?.balvatika ?? 0, e.counts?.primary ?? 0, e.counts?.upper_primary ?? 0, e.counts?.high_school ?? 0,
      e.summary?.total_students ?? 0, Number(e.summary?.total_rice_kg || 0).toFixed(3), Number(e.summary?.grand_total || 0).toFixed(2),
    ]);
    const csv = [header, ...rows, [], ["", "", "", "", "GRAND TOTAL", "", "", "", "", totals.students, totals.rice.toFixed(3), totals.cost.toFixed(2)]]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    _downloadCsv(csv, `PM_POSHAN_${tab.toUpperCase()}_${iso(range.from)}_${iso(range.to)}.csv`);
  };

  const _downloadCsv = (csv, filename) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const onPrint = () => window.print();

  if (loading || !user) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const premium = !!sub?.is_premium;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="chip chip-green"><BarChart3 size={14}/> Reports</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Weekly, Monthly & Annual reports</h1>
          <p className="mt-1 text-slate-600">Rolled up from your saved Daily Entries. Export PDF or CSV in one tap.</p>
        </div>
        {!premium && (
          <Button onClick={() => nav("/premium")} className="bg-orange-600 hover:bg-orange-700 text-white h-11" data-testid="reports-upgrade">
            <Sparkles size={16}/> Unlock Premium
          </Button>
        )}
        {premium && (
          <Button onClick={() => nav("/monthly-mdcf")} variant="outline" className="h-11" data-testid="reports-mdcf-link">
            📋 Monthly MDCF
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5" data-testid="reports-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual">Annual</TabsTrigger>
          <TabsTrigger value="class" data-testid="tab-class">Class-Wise</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 print-report">
            {tab === "class" ? (
              <ClassWiseControls
                cat={cwCat} setCat={setCwCat}
                from={cwFrom} setFrom={setCwFrom}
                to={cwTo} setTo={setCwTo}
              />
            ) : (
              <div className="flex items-center justify-between gap-2">
                <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(-1)} data-testid="reports-prev"><ChevronLeft size={16}/></Button>
                <div className="text-center font-display font-bold text-lg text-slate-900 flex-1" data-testid="reports-range-label">
                  {tab === "daily" ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="inline-flex items-center gap-1 hover:underline"><CalendarIcon size={14} className="text-emerald-700"/> {formatReportDate(anchor)}</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="center">
                        <ShadcnCal mode="single" selected={anchor} onSelect={(d) => d && setAnchor(d)} fromYear={2020} toYear={2100} captionLayout="dropdown-buttons"/>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <><CalendarIcon size={14} className="inline text-emerald-700 mr-1"/> {range.label}</>
                  )}
                </div>
                <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(1)} data-testid="reports-next"><ChevronRight size={16}/></Button>
              </div>
            )}

            {tab === "daily" ? (
              <DailyReportCard entry={entries.find((e) => (e.entry_type || "meal") === "meal")} school={{ name: user?.school_name || "", udise: user?.udise || "" }} date={anchor}/>
            ) : tab === "class" ? (
              <ClassWiseReport entries={entries} cat={cwCat} school={{ name: user?.school_name || "", udise: user?.udise || "" }} from={cwFrom} to={cwTo}/>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  <Kpi icon={<CalendarIcon size={16}/>} label="Working Days" value={totals.workingDays} tint="emerald" testid="rep-kpi-working"/>
                  <Kpi icon={<span>🔴</span>} label="Holidays" value={totals.holidays} tint="red" testid="rep-kpi-holidays"/>
                  <Kpi icon={<span>🟢</span>} label="MDM Days" value={totals.mdmDays} tint="emerald" testid="rep-kpi-mdm"/>
                  <Kpi icon={<span>🔵</span>} label="Dry Ration" value={totals.dryDays} tint="sky" testid="rep-kpi-dry"/>
                  <Kpi icon={<Users size={16}/>} label="Meals served" value={totals.students} tint="emerald" testid="rep-kpi-meals"/>
                  <Kpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(totals.cost)} tint="orange" testid="rep-kpi-cost"/>
                  <Kpi icon={<Wheat size={16}/>} label="Rice" value={formatRiceQuantity(totals.rice)} tint="emerald" testid="rep-kpi-rice"/>
                </div>
                <CategoryBreakdownCard entries={entries}/>
              </>
            )}

            <div className="mt-4 flex flex-wrap gap-2 justify-end print:hidden">
              <Button onClick={onPrint} variant="outline" data-testid="reports-print-btn"><Printer size={14}/> Print</Button>
              <Button onClick={onCsv} variant="outline" data-testid="reports-csv-btn"><Table2 size={14}/> Export CSV</Button>
              <Button onClick={onPdf} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="reports-pdf-btn"><FileDown size={14}/> Export PDF</Button>
            </div>

            {tab === "daily" || tab === "class" ? null : tab === "annual" ? (
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <Th>Month</Th><Th className="text-center">Days</Th><Th className="text-center">Students</Th><Th className="text-center">🧁 Ladoos</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost (₹)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthGroups.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-slate-500">No entries in this academic year.</td></tr>
                    )}
                    {monthGroups.map((g) => (
                      <tr key={g.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rep-annual-row-${g.key}`}>
                        <Td className="font-semibold text-slate-900">{g.label}</Td>
                        <Td className="text-center">{g.days}</Td>
                        <Td className="text-center">{g.students}</Td>
                        <Td className="text-center">{g.ladoos}</Td>
                        <Td className="text-right">{formatRiceKg(g.rice)}</Td>
                        <Td className="text-right font-semibold text-slate-900">{formatCurrency(g.cost)}</Td>
                      </tr>
                    ))}
                  </tbody>
                  {monthGroups.length > 0 && (
                    <tfoot>
                      <tr className="bg-emerald-50">
                        <Td className="font-bold text-emerald-900">Annual total</Td>
                        <Td className="text-center font-bold text-emerald-900">{totals.days}</Td>
                        <Td className="text-center font-bold text-emerald-900">{totals.students}</Td>
                        <Td className="text-center font-bold text-emerald-900">{totals.ladoos}</Td>
                        <Td className="text-right font-bold text-emerald-900">{formatRiceKg(totals.rice)}</Td>
                        <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="rep-annual-total">{formatCurrency(totals.cost)}</Td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <Th>Date</Th><Th>Day</Th><Th>Type</Th><Th>Menu</Th><Th className="text-center">Students</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost (₹)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 && (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="reports-empty">No entries in this range.</td></tr>
                    )}
                    {entries.map((e) => {
                      const st = classify(e);
                      return (
                        <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${st.key==="ladoo" ? "bg-orange-50/40" : st.key==="holiday" ? "bg-red-50/40" : st.key==="dry" ? "bg-sky-50/40" : ""}`} data-testid={`rep-row-${e.id}`}>
                          <Td className="font-semibold text-slate-900">{formatReportDate(e.date_iso)}</Td>
                          <Td>{e.weekday_name}</Td>
                          <Td><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.dot} {st.label}</span></Td>
                          <Td className="text-slate-600 max-w-[240px] truncate" title={e.menu_display}>{e.menu_display}</Td>
                          <Td className="text-center font-semibold">{e.summary?.total_students || 0}</Td>
                          <Td className="text-right">{formatRiceKg(e.summary?.total_rice_kg || 0)}</Td>
                          <Td className="text-right font-semibold text-slate-900">{formatCurrency(e.summary?.grand_total || 0)}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {entries.length > 0 && (
                    <tfoot>
                      <tr className="bg-emerald-50">
                        <Td colSpan={4} className="font-bold text-emerald-900 text-right">Period total ({totals.days} days · avg {formatCurrency(totals.avg_per_day)}/day)</Td>
                        <Td className="text-center font-bold text-emerald-900">{totals.students}</Td>
                        <Td className="text-right font-bold text-emerald-900">{formatRiceKg(totals.rice)}</Td>
                        <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="rep-grand-total">{formatCurrency(totals.cost)}</Td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50"
    : tint === "orange" ? "border-orange-200 bg-orange-50"
    : tint === "sky" ? "border-sky-200 bg-sky-50"
    : tint === "red" ? "border-red-200 bg-red-50"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
    </div>
  );
}
const Th = ({ children, className = "" }) => <th className={`p-3 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`p-3 text-slate-700 ${className}`}>{children}</td>;

function DailyReportCard({ entry, school, date }) {
  const st = classify(entry);
  const s = entry?.summary || {};
  const cats = CATEGORY_KEYS.map((key) => ({ key, label: CATEGORY_LABELS[key], emoji: CATEGORY_EMOJI[key] }));
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="daily-report-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 border-b border-slate-100 pb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Daily PM POSHAN Report</div>
          <h2 className="mt-1 font-display font-extrabold text-xl text-slate-900">{school.name || "—"}</h2>
          <p className="text-xs text-slate-600">UDISE: {school.udise || "—"}</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-600">{formatReportDate(date)}</div>
          <span className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>{st.dot} {st.label}</span>
        </div>
      </div>

      {!entry && (
        <div className="mt-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center text-slate-600" data-testid="daily-report-empty">
          ⚪ No entry has been saved for this date yet.
        </div>
      )}

      {entry && st.key === "holiday" && (
        <div className="mt-6 rounded-xl border-2 border-red-200 bg-red-50 p-8 text-center" data-testid="daily-report-holiday">
          <div className="font-display font-extrabold text-red-900">🔴 Holiday</div>
          <p className="mt-1 text-sm text-red-800">No MDM / Dry Ration entry was recorded for this date.</p>
        </div>
      )}

      {entry && st.key !== "holiday" && (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi icon={<Users size={16}/>} label="Total attendance" value={s.total_students || 0} tint="emerald"/>
            <Kpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(s.grand_total || 0)} tint="orange"/>
            <Kpi icon={<Wheat size={16}/>} label="Rice" value={formatRiceQuantity(s.total_rice_kg || 0)} tint="emerald"/>
            <Kpi icon={<CalendarIcon size={16}/>} label={st.key === "dry" ? "Type" : "Menu"} value={st.key === "dry" ? "Dry Ration" : (entry.menu_display || "—")}/>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Category</Th><Th className="text-center">Students</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost</Th></tr>
              </thead>
              <tbody>
                {cats.map((c) => {
                  const cd = entry.categories?.[c.key] || {};
                  return (
                    <tr key={c.key} className="border-b border-slate-100">
                      <Td className="font-semibold text-slate-900">{c.emoji} {c.label}</Td>
                      <Td className="text-center">{cd.student_count ?? entry.counts?.[c.key] ?? 0}</Td>
                      <Td className="text-right">{formatRiceKg(cd.rice_kg)}</Td>
                      <Td className="text-right">{formatCurrency(cd.subtotal || 0)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Class-Wise Report ─────────────────────────────────────────────────────
function ClassWiseControls({ cat, setCat, from, setFrom, to, setTo }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="classwise-controls">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Category</div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
          className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900"
          data-testid="classwise-category-select">
          <option value="all">All Categories</option>
          {CATEGORY_KEYS.map((k) => <option key={k} value={k}>{CATEGORY_EMOJI[k]} {CATEGORY_LABELS[k]}</option>)}
        </select>
      </div>
      <DatePickerField label="From date" value={from} onChange={setFrom} testid="classwise-from"/>
      <DatePickerField label="To date" value={to} onChange={setTo} testid="classwise-to"/>
    </div>
  );
}

function DatePickerField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="mt-1 h-11 w-full justify-start font-semibold" data-testid={testid}>
            <CalendarIcon size={14}/> {formatReportDate(value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <ShadcnCal mode="single" selected={value} onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }} fromYear={2020} toYear={2100} captionLayout="dropdown-buttons"/>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ClassWiseReport({ entries, cat, school, from, to }) {
  const rows = entries.filter((e) => (e.entry_type || "meal") === "meal");
  const perCat = useMemo(() => {
    const out = {};
    CATEGORY_KEYS.forEach((k) => { out[k] = aggregateCategory(rows, k); });
    return out;
  }, [rows]);
  const totalAtt = CATEGORY_KEYS.reduce((sum, k) => sum + perCat[k].attendance, 0);
  const totalRice = CATEGORY_KEYS.reduce((sum, k) => sum + perCat[k].rice_kg, 0);
  const totalCost = CATEGORY_KEYS.reduce((sum, k) => sum + perCat[k].cost, 0);

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="classwise-report">
      <div className="border-b border-slate-100 pb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Class-Wise PM POSHAN Report</div>
        <h2 className="mt-1 font-display font-extrabold text-xl text-slate-900">{school.name || "—"}</h2>
        <p className="text-xs text-slate-600">UDISE: {school.udise || "—"} · Period: {formatReportDate(from)} to {formatReportDate(to)}</p>
        {cat !== "all" && <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[11px] font-bold" data-testid="classwise-cat-badge">{CATEGORY_EMOJI[cat]} {CATEGORY_LABELS[cat]}</span>}
      </div>

      {cat === "all" ? (
        <>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CATEGORY_KEYS.map((k) => {
              const a = perCat[k];
              return (
                <div key={k} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3" data-testid={`classwise-card-${k}`}>
                  <div className="text-sm font-bold text-slate-900">{CATEGORY_EMOJI[k]} {CATEGORY_LABELS[k]}</div>
                  <div className="mt-1 text-xs text-slate-600">Attendance</div>
                  <div className="font-display font-extrabold text-lg text-slate-900">{a.attendance}</div>
                  <div className="mt-1 text-xs text-slate-600">Rice · {formatRiceQuantity(a.rice_kg)}</div>
                  <div className="text-xs text-slate-600">Cost · {formatCurrency(a.cost)}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Category</Th><Th className="text-center">Working Days</Th><Th className="text-center">Attendance</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost</Th></tr>
              </thead>
              <tbody>
                {CATEGORY_KEYS.map((k) => {
                  const a = perCat[k];
                  return (
                    <tr key={k} className="border-b border-slate-100" data-testid={`classwise-row-${k}`}>
                      <Td className="font-semibold text-slate-900">{CATEGORY_EMOJI[k]} {CATEGORY_LABELS[k]}</Td>
                      <Td className="text-center">{a.working_days}</Td>
                      <Td className="text-center">{a.attendance}</Td>
                      <Td className="text-right">{formatRiceKg(a.rice_kg)}</Td>
                      <Td className="text-right font-semibold">{formatCurrency(a.cost)}</Td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-50">
                  <Td className="font-bold text-emerald-900">TOTAL</Td>
                  <Td/>
                  <Td className="text-center font-bold text-emerald-900" data-testid="classwise-total-attendance">{totalAtt}</Td>
                  <Td className="text-right font-bold text-emerald-900" data-testid="classwise-total-rice">{formatRiceKg(totalRice)}</Td>
                  <Td className="text-right font-display font-extrabold text-emerald-800" data-testid="classwise-total-cost">{formatCurrency(totalCost)}</Td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <ClassWiseDetail entries={rows} cat={cat} agg={perCat[cat]}/>
      )}
    </div>
  );
}

function ClassWiseDetail({ entries, cat, agg }) {
  return (
    <>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={<CalendarIcon size={16}/>} label="Working Days" value={agg.working_days} tint="emerald"/>
        <Kpi icon={<span>🔴</span>} label="Holidays" value={agg.holidays} tint="red"/>
        <Kpi icon={<Users size={16}/>} label="Attendance" value={agg.attendance} tint="emerald"/>
        <Kpi icon={<IndianRupee size={16}/>} label="Cost" value={formatCurrency(agg.cost)} tint="orange"/>
      </div>
      <div className="mt-2 text-xs text-slate-600">Avg attendance / working day: <b>{agg.avg.toFixed(1)}</b> · Rice: <b>{formatRiceQuantity(agg.rice_kg)}</b></div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><Th>Date</Th><Th>Day Type</Th><Th>Meal</Th><Th className="text-center">Attendance</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost</Th></tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500" data-testid="classwise-empty">No entries in this range.</td></tr>}
            {entries.map((e) => {
              const st = classify(e);
              const c = (e.categories || {})[cat] || {};
              const isHol = e.day_type === "HOLIDAY";
              return (
                <tr key={e.id} className={`border-b border-slate-100 ${st.key==="holiday"?"bg-red-50/40":st.key==="dry"?"bg-sky-50/40":""}`} data-testid={`classwise-detail-${e.id}`}>
                  <Td className="font-semibold text-slate-900">{formatReportDate(e.date_iso)}</Td>
                  <Td><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.dot} {st.label}</span></Td>
                  <Td>{isHol ? "—" : (e.meal_type === "DRY_RATION" ? "Dry Ration" : "MDM")}</Td>
                  <Td className="text-center">{isHol ? "—" : (c.student_count ?? e.counts?.[cat] ?? 0)}</Td>
                  <Td className="text-right">{isHol ? "0.000" : formatRiceKg(c.rice_kg)}</Td>
                  <Td className="text-right">{isHol ? formatCurrency(0) : formatCurrency(c.subtotal || 0)}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Small "category breakdown" summary shown under weekly/monthly/annual KPIs
function CategoryBreakdownCard({ entries }) {
  const perCat = useMemo(() => {
    const out = {};
    CATEGORY_KEYS.forEach((k) => { out[k] = aggregateCategory(entries, k); });
    return out;
  }, [entries]);
  const total = CATEGORY_KEYS.reduce((s, k) => s + perCat[k].attendance, 0);
  if (!entries.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="category-breakdown-card">
      <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800 mb-2">Category breakdown</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[10px] uppercase font-bold text-slate-500">
              <Th>Category</Th><Th className="text-center">Working Days</Th><Th className="text-center">Attendance</Th><Th className="text-right">Rice (kg)</Th><Th className="text-right">Cost</Th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_KEYS.map((k) => {
              const a = perCat[k];
              return (
                <tr key={k} className="border-t border-emerald-100" data-testid={`breakdown-${k}`}>
                  <Td className="font-semibold">{CATEGORY_EMOJI[k]} {CATEGORY_LABELS[k]}</Td>
                  <Td className="text-center">{a.working_days}</Td>
                  <Td className="text-center">{a.attendance}</Td>
                  <Td className="text-right">{formatRiceKg(a.rice_kg)}</Td>
                  <Td className="text-right font-semibold">{formatCurrency(a.cost)}</Td>
                </tr>
              );
            })}
            <tr className="border-t border-emerald-200 bg-emerald-100/60">
              <Td className="font-bold text-emerald-900">TOTAL</Td>
              <Td/>
              <Td className="text-center font-bold text-emerald-900">{total}</Td>
              <Td className="text-right font-bold text-emerald-900">{formatRiceKg(CATEGORY_KEYS.reduce((s,k)=>s+perCat[k].rice_kg,0))}</Td>
              <Td className="text-right font-display font-extrabold text-emerald-800">{formatCurrency(CATEGORY_KEYS.reduce((s,k)=>s+perCat[k].cost,0))}</Td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
